import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { Alert, Box, Typography, CircularProgress, useTheme, useMediaQuery, IconButton, Fade, Dialog } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import Sidebar from './components/Sidebar';
import SuccessPopover from './components/SuccessPopover';
import Login from './components/Login';
import PasswordResetModal from './components/PasswordResetModal';
import Landing from './components/Landing';
import PublicHelpAndSupport from './components/PublicHelpAndSupport';
import Unsubscribe from './components/Unsubscribe';
import SubscriptionCancel from './components/SubscriptionCancel';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import CheckoutStart from './components/CheckoutStart';
import AppLayout from './layouts/AppLayout';
import PublicLayout from './layouts/PublicLayout';
import SetupLayout from './layouts/SetupLayout';
import SubscriptionLayout from './layouts/SubscriptionLayout';
import CheckoutLayout from './layouts/CheckoutLayout';
import AppRoutes from './routes/AppRoutes';
import AuthRequired from './routes/AuthRequired';
import SubscriptionRequiredPage from './pages/SubscriptionRequiredPage';
import ChannelSetupPage from './pages/ChannelSetupPage';
import { pagePaddingX, pagePaddingY } from './layouts/layoutSpacing';
import useSubscriptionData from './hooks/useSubscriptionData';
import useAuthSession from './hooks/useAuthSession';
import useSetupFlow from './hooks/useSetupFlow';
import useSubscriptionGate from './hooks/useSubscriptionGate';
import usePendingCheckoutRedirect from './hooks/usePendingCheckoutRedirect';
import useQuotaUsage from './hooks/useQuotaUsage';
import useExpensesData from './hooks/useExpensesData';
import useMileageData from './hooks/useMileageData';
import useChannelsData from './hooks/useChannelsData';
import useSalesTransactionsData from './hooks/useSalesTransactionsData';
import useIntegrationData from './hooks/useIntegrationData';
import useQuailHQData from './hooks/useQuailHQData';
import usePageTracking from './hooks/usePageTracking';
import { trackFunnelStep } from './analytics';

const AIAssistant = React.lazy(() => import('./components/AIAssistant'));

const API_BACKEND = process.env.REACT_APP_SHOPKEEP_BACKEND || 'http://localhost:3001';

const ShopKeepApp = () => {
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [oobCode, setOobCode] = useState(null);
  const [resetSuccessEmail, setResetSuccessEmail] = useState(null);
  const [showLoginOnResetRequest, setShowLoginOnResetRequest] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(false);
  const [pendingPriceId, setPendingPriceId] = useState(null);
  const [needsInitialChannel, setNeedsInitialChannel] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const channelsCheckRef = useRef(false);
  const successTimeoutRef = useRef(null);

  // Check if mobile on first render
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isMobileScreen);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const isCheckoutRoute = ['/checkout-start', '/subscription-success', '/subscription-cancel'].includes(currentPath);
  const isSubscriptionRequiredRoute = currentPath === '/subscription-required';
  const isChannelManagement = currentPath === '/channels';
  const isReports = currentPath === '/reports';
  const isImportData = currentPath === '/import-data';
  const isExportData = currentPath === '/export-data';
  const isAccountManagement = currentPath === '/account';
  const isSalesTransactions = currentPath === '/sales-transactions';
  const isExpenses = currentPath === '/expenses';
  const isMileage = currentPath === '/mileage';
  const isHelpAndSupport = currentPath === '/help';

  // Google Analytics page tracking
  usePageTracking();

  // Clear error messages when navigating to a different page
  useEffect(() => {
    setError('');
  }, [currentPath]);

  const { user, setUser, authLoading, getAuthHeaders, handleLogout: authHandleLogout } = useAuthSession({
    auth,
    setError,
  });

  // Detect Firebase password reset action URL and open the reset modal.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname !== '/__/auth/action') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') !== 'resetPassword') return;
    const code = params.get('oobCode');
    if (!code) return;
    if (user) {
      // Sign out before showing the reset modal; the effect will re-run when user becomes null.
      signOut(auth).catch((err) => console.error('Sign-out before reset failed:', err));
      return;
    }
    setOobCode(code);
    setShowPasswordResetModal(true);
  }, [user]);


  const {
    subscriptionStatus,
    customerDetails,
    hasActiveSubscription,
    isCreatingCheckout,
    createCheckoutSession,
    checkSubscriptionActive,
    fetchCustomerDetails,
  } = useSubscriptionData({
    apiBackend: API_BACKEND,
    user,
    getAuthHeaders,
    setError,
  });

  const accountId = customerDetails?.accountId;

  const { quotaUsage, loadingQuota, fetchQuotaUsage } = useQuotaUsage({
    user,
    getAuthHeaders,
    hasActiveSubscription,
  });

  const showSuccessMessage = useCallback((message) => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    setSuccessMessage(message);
    setShowSuccess(true);
    successTimeoutRef.current = setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage('');
      successTimeoutRef.current = null;
    }, 5000);
  }, []);

  const {
    channels: sharedChannels,
    isLoadingChannels,
    showChannelForm,
    setShowChannelForm,
    channelFormData,
    setChannelFormData,
    editingChannel,
    setEditingChannel,
    fetchChannels: fetchSharedChannels,
    handleChannelInputChange,
    handleChannelSubmit,
    startEditingChannel,
    cancelChannelEdit,
    deleteChannel,
    isSubmitting: isChannelSubmitting,
    fetchNextItemNumber,
  } = useChannelsData({
    salesChannelsUrl: `${API_BACKEND}/api/saleschannels`,
    getAuthHeaders,
    pendingCheckout,
    showSuccessMessage,
    setError,
    needsInitialChannel,
    setNeedsInitialChannel,
    accountId,
  });

  const {
    expenses,
    isLoadingExpenses,
    expensesError,
    setExpensesError,
    fetchExpenses,
    createExpense,
    updateExpense,
    deleteExpense: deleteExpenseFn,
    selectedMonth: expensesMonth,
    setSelectedMonth: setExpensesMonth,
    monthOptions: expensesMonthOptions,
  } = useExpensesData({
    expensesUrl: `${API_BACKEND}/api/expenses`,
    getAuthHeaders,
    accountId,
    showSuccessMessage,
  });

  const {
    selectedYear: mileageYear,
    setSelectedYear: setMileageYear,
    trips: mileageTrips,
    odometerYearStart,
    odometerYearEnd,
    defaultMileageRate,
    isLoading: isLoadingMileage,
    error: mileageError,
    setError: setMileageError,
    fetchMileage,
    createTrip,
    updateTrip,
    deleteTrip: deleteTripFn,
    updateOdometer,
    updateMileageRate,
  } = useMileageData({
    mileageUrl: `${API_BACKEND}/api/mileage`,
    getAuthHeaders,
    accountId,
    user,
  });

  const {
    transactions,
    isLoadingTransactions,
    transactionsError,
    fetchTransactions,
    updateTransactionDate,
    selectedMonth: transactionsMonth,
    setSelectedMonth: setTransactionsMonth,
    monthOptions: transactionsMonthOptions,
    selectedChannel: transactionsChannel,
    setSelectedChannel: setTransactionsChannel,
  } = useSalesTransactionsData({
    salesTransactionsUrl: `${API_BACKEND}/api/salestransactions`,
    getAuthHeaders,
    showSuccessMessage,
  });

  const {
    integrationStatuses,
    platforms,
    isLoadingStatuses,
    isConnecting,
    isDisconnecting,
    isSyncing,
    isImporting,
    isPulling,
    lastSyncResult,
    lastImportResult,
    lastPullResult,
    conflicts,
    isLoadingConflicts,
    isResolvingConflict,
    fetchPlatforms,
    fetchIntegrationStatuses,
    connectChannel,
    disconnectChannel,
    syncInventory,
    importSales,
    pullInventory,
    fetchConflicts,
    resolveConflict,
    resolveAllConflicts,
    clearResults,
  } = useIntegrationData({
    getAuthHeaders,
    apiBackend: API_BACKEND,
    setError,
    showSuccessMessage,
  });

  const {
    isRequestingBarcodes,
    quailhqStatuses,
    isLoadingQuailHQStatus,
    fetchQuailHQStatus,
    requestBarcodes,
  } = useQuailHQData({
    getAuthHeaders,
    apiBackend: API_BACKEND,
    setError,
    showSuccessMessage,
  });

  const handleLogout = useCallback(async () => {
    await authHandleLogout();
  }, [authHandleLogout]);

  const waitForSubscriptionActivation = async (maxWaitTime = 30000) => {
    const startTime = Date.now();
    const pollInterval = 1000;

    while (Date.now() - startTime < maxWaitTime) {
      const isActive = await checkSubscriptionActive();
      if (isActive) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    return false;
  };

  const handleCheckoutSuccess = async () => {
    try {
      sessionStorage.removeItem('checkout_in_progress');
    } catch (e) {
      // ignore storage errors
    }

    const activated = await waitForSubscriptionActivation(30000);
    if (activated) {
      await fetchCustomerDetails();
      channelsCheckRef.current = true;
      setNeedsInitialChannel(true);
      navigate('/setup/channel');
      return { success: true };
    }

    return {
      success: false,
      message: 'We are still confirming your subscription. Please wait a moment and try again.',
    };
  };

  const handleCheckoutCancel = () => {
    (async () => {
      try {
        await handleLogout();
      } finally {
        try {
          sessionStorage.removeItem('checkout_in_progress');
        } catch (e) {
          // ignore storage errors
        }
        navigate('/', { replace: true });
      }
    })();
  };


  // Fetch customer details on login and refresh when subscription is active
  useEffect(() => {
    if (!user) return;

    // Always fetch on initial login (hasActiveSubscription is null)
    if (hasActiveSubscription === null) {
      fetchCustomerDetails();
      return;
    }

    // Refresh when subscription is active and not on checkout/subscription routes
    if (hasActiveSubscription === true && !isCheckoutRoute && !isSubscriptionRequiredRoute && !pendingCheckout) {
      fetchCustomerDetails();
    }
  }, [user, hasActiveSubscription, isCheckoutRoute, isSubscriptionRequiredRoute, pendingCheckout, fetchCustomerDetails]);

  usePendingCheckoutRedirect({
    user,
    pendingCheckout,
    setPendingCheckout,
    navigate,
  });

  useSetupFlow({
    needsInitialChannel,
    pendingCheckout,
    currentPath,
    navigate,
    hasActiveSubscription,
  });

  useSubscriptionGate({
    user,
    hasActiveSubscription,
    isCheckoutRoute,
    isSubscriptionRequiredRoute,
    currentPath,
    navigate,
  });

  // After channels are fetched, check if user needs initial channel setup
  useEffect(() => {
    if (!user || hasActiveSubscription !== true) {
      channelsCheckRef.current = false;
      return;
    }
    if (pendingCheckout || isCheckoutRoute || isSubscriptionRequiredRoute) {
      return;
    }
    if (needsInitialChannel || channelsCheckRef.current) {
      return;
    }
    // Wait until channels have been fetched (non-empty or loading complete)
    if (sharedChannels === null) return;

    channelsCheckRef.current = true;
    if (sharedChannels.length === 0) {
      setNeedsInitialChannel(true);
      navigate('/setup/channel', { replace: true });
    }
  }, [
    user,
    hasActiveSubscription,
    pendingCheckout,
    isCheckoutRoute,
    isSubscriptionRequiredRoute,
    needsInitialChannel,
    sharedChannels,
    navigate,
  ]);

  useEffect(() => {
    if (hasActiveSubscription === false) {
      setNeedsInitialChannel(false);
    }
  }, [hasActiveSubscription]);

  // Navigate to inventory after initial channel is created
  const prevNeedsInitialChannelRef = useRef(needsInitialChannel);
  useEffect(() => {
    if (prevNeedsInitialChannelRef.current === true && needsInitialChannel === false && hasActiveSubscription === true) {
      trackFunnelStep('sign_up_complete');
      navigate('/', { replace: true });
    }
    prevNeedsInitialChannelRef.current = needsInitialChannel;
  }, [needsInitialChannel, hasActiveSubscription, navigate]);

  useEffect(() => {
    if (user && hasActiveSubscription) {
      fetchSharedChannels();
    }
  }, [user, hasActiveSubscription, fetchSharedChannels]);

  useEffect(() => {
    if (user && hasActiveSubscription && isSalesTransactions) {
      fetchTransactions();
    }
  }, [user, hasActiveSubscription, isSalesTransactions, fetchTransactions]);

  useEffect(() => {
    let checkoutInProgress = false;
    try {
      checkoutInProgress = sessionStorage.getItem('checkout_in_progress') === 'true';
    } catch (e) {
      checkoutInProgress = false;
    }

    if (currentPath === '/subscription-success' || currentPath === '/subscription-cancel') {
      try {
        sessionStorage.removeItem('checkout_in_progress');
      } catch (e) {
        // ignore storage errors
      }
      return;
    }

    if (user && checkoutInProgress && !isCheckoutRoute) {
      (async () => {
        try {
          await handleLogout();
        } finally {
          try {
            sessionStorage.removeItem('checkout_in_progress');
          } catch (e) {
            // ignore storage errors
          }
          navigate('/', { replace: true });
        }
      })();
    }
  }, [user, currentPath, isCheckoutRoute, handleLogout, navigate]);

  // Cleanup success message timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  async function handleExportData(categoryIds) {
    const axios = (await import('axios')).default;
    const config = await getAuthHeaders();
    const results = await Promise.allSettled(
      categoryIds.map(async (id) => {
        const endpoint = id === 'mileage'
          ? `${API_BACKEND}/api/export/mileage/${new Date().getFullYear()}`
          : id === 'inventory-pos'
          ? `${API_BACKEND}/api/export/inventory/pos`
          : `${API_BACKEND}/api/export/${id}`;
        const response = await axios.get(endpoint, { ...config, responseType: 'blob' });
        const disposition = response.headers['content-disposition'] || '';
        const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
        const filename = filenameMatch ? filenameMatch[1] : `${id}-export-${new Date().toISOString().slice(0, 10)}.csv`;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return { categoryId: id, success: true };
      })
    );
    return results.map((r, i) =>
      r.status === 'fulfilled' ? r.value : { categoryId: categoryIds[i], success: false, error: r.reason?.message }
    );
  }

  if (authLoading) {
    return (
      <Fade in={true} timeout={400}>
        <Box sx={{ display: 'flex', fontFamily: 'inherit', minHeight: '100vh', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Fade>
    );
  }

  // Show loading state while pending checkout is being processed
  if (pendingCheckout && user) {
    return (
      <Fade in={true} timeout={400}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'heading.main' }}>
              Setting up your free trial...
            </Typography>
            <Typography variant="body2" sx={{ color: 'textSecondary', mt: 1 }}>
              Redirecting to Stripe Checkout...
            </Typography>
          </Box>
        </Box>
      </Fade>
    );
  }

  if (user && hasActiveSubscription === null && !isCheckoutRoute && !isSubscriptionRequiredRoute) {
    return (
      <Fade in={true} timeout={400}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={56} sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'heading.main' }}>
              Checking subscription status...
            </Typography>
          </Box>
        </Box>
      </Fade>
    );
  }

  if (!user) {
    return (
      <>
      <Routes>
        {[
          { path: '/' },
          { path: '/pricing', section: 'pricing' },
          { path: '/features', section: 'features' },
        ].map(({ path, section }) => (
          <Route
            key={path}
            path={path}
            element={
              <PublicLayout>
                <Landing
                  pageKey={path}
                  section={section}
                  onLoginClick={() => setShowLoginModal(true)}
                  onStartFreeTrial={(priceId) => {
                    setPendingPriceId(priceId);
                    setPendingCheckout(true);
                    setShowLoginModal(true);
                  }}
                  error={error}
                  onClearError={() => setError('')}
                />
              </PublicLayout>
            }
          />
        ))}
        <Route
          path="/help"
          element={
            <PublicLayout>
              <PublicHelpAndSupport
                onLoginClick={() => setShowLoginModal(true)}
              />
            </PublicLayout>
          }
        />
        <Route
          path="/unsubscribe"
          element={
            <PublicLayout>
              <Unsubscribe />
            </PublicLayout>
          }
        />
        <Route path="/checkout-start" element={<Navigate to="/" replace />} />
        <Route path="/subscription-success" element={<Navigate to="/" replace />} />
        <Route path="/subscription-cancel" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Dialog
        open={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          setResetSuccessEmail(null);
          setShowLoginOnResetRequest(false);
        }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="login-dialog-title"
        PaperProps={{ sx: { background: 'transparent', boxShadow: 'none' } }}
      >
        <Login
          onLoginSuccess={(user) => setUser(user)}
          initialEmail={resetSuccessEmail || ''}
          startOnEmailSignIn={!!resetSuccessEmail}
          startOnResetRequest={showLoginOnResetRequest}
        />
      </Dialog>
      <Dialog
        open={showPasswordResetModal}
        onClose={() => {
          setShowPasswordResetModal(false);
          setOobCode(null);
          if (typeof window !== 'undefined') {
            window.history.replaceState({}, '', '/');
          }
        }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="password-reset-dialog-title"
        PaperProps={{ sx: { background: 'transparent', boxShadow: 'none' } }}
      >
        <PasswordResetModal
          oobCode={oobCode}
          onClose={() => {
            setShowPasswordResetModal(false);
            setOobCode(null);
            if (typeof window !== 'undefined') {
              window.history.replaceState({}, '', '/');
            }
          }}
          onSuccess={(email) => {
            setShowPasswordResetModal(false);
            setOobCode(null);
            if (typeof window !== 'undefined') {
              window.history.replaceState({}, '', '/');
            }
            setResetSuccessEmail(email);
            setShowLoginOnResetRequest(false);
            setShowLoginModal(true);
          }}
          onRequestNewLink={() => {
            setShowPasswordResetModal(false);
            setOobCode(null);
            if (typeof window !== 'undefined') {
              window.history.replaceState({}, '', '/');
            }
            setResetSuccessEmail(null);
            setShowLoginOnResetRequest(true);
            setShowLoginModal(true);
          }}
        />
      </Dialog>
      </>
    );
  }

  if (user && hasActiveSubscription === false && !isCheckoutRoute) {
    return (
      <Routes>
        <Route
          path="/subscription-required"
          element={
            <SubscriptionLayout>
              <SubscriptionRequiredPage
                isCreatingCheckout={isCreatingCheckout}
                onStartTrial={(priceId) => createCheckoutSession(priceId)}
                onLogout={handleLogout}
              />
            </SubscriptionLayout>
          }
        />
        <Route path="*" element={<Navigate to="/subscription-required" replace />} />
      </Routes>
    );
  }

  if (currentPath === '/checkout-start' || currentPath === '/subscription-cancel' || currentPath === '/subscription-success') {
    return (
      <Fade in={true} timeout={400}>
      <Box>
      <Routes>
        <Route
          path="/checkout-start"
          element={
            <CheckoutLayout>
              <CheckoutStart onInitiateCheckout={() => createCheckoutSession(pendingPriceId)} />
            </CheckoutLayout>
          }
        />
        <Route
          path="/subscription-success"
          element={
            <CheckoutLayout>
              <SubscriptionSuccess onContinue={handleCheckoutSuccess} subscriptionPlanName={subscriptionStatus?.plan} />
            </CheckoutLayout>
          }
        />
        <Route
          path="/subscription-cancel"
          element={
            <CheckoutLayout>
              <SubscriptionCancel onReturnToHome={handleCheckoutCancel} />
            </CheckoutLayout>
          }
        />
        <Route path="*" element={<Navigate to="/checkout-start" replace />} />
      </Routes>
      </Box>
      </Fade>
    );
  }

  if (hasActiveSubscription === true && needsInitialChannel) {
    return (
      <Fade in={true} timeout={400}>
      <Box>
      <Routes>
        <Route
          path="/setup/channel"
          element={
            <SetupLayout title="Sales Channel Setup">
              <ChannelSetupPage
                channelFormData={channelFormData}
                handleChannelInputChange={handleChannelInputChange}
                handleChannelSubmit={handleChannelSubmit}
                isSubmitting={isChannelSubmitting}
                error={error}
              />
            </SetupLayout>
          }
        />
        <Route
          path="*"
          element={<Navigate to="/setup/channel" replace />}
        />
      </Routes>
      </Box>
      </Fade>
    );
  }

  return (
    <AuthRequired user={user}>
      <AppLayout
      sidebar={
        <Sidebar
          user={user}
          showChannelManagement={isChannelManagement}
          onManageInventoryClick={() => {
            navigate('/');
          }}
          onManageChannelsClick={() => {
            navigate('/channels');
          }}
          showReports={isReports}
          onReportsClick={() => {
            navigate('/reports');
          }}
          showImportData={isImportData}
          onImportDataClick={() => {
            navigate('/import-data');
          }}
          showExportData={isExportData}
          onExportDataClick={() => {
            navigate('/export-data');
          }}
          showAccountManagement={isAccountManagement}
          onAccountManagementClick={() => {
            navigate('/account');
          }}
          showSalesTransactions={isSalesTransactions}
          onSalesTransactionsClick={() => {
            navigate('/sales-transactions');
          }}
          showExpenses={isExpenses}
          onExpensesClick={() => {
            navigate('/expenses');
          }}
          showMileage={isMileage}
          onMileageClick={() => {
            navigate('/mileage');
          }}
          showHelpAndSupport={isHelpAndSupport}
          onHelpAndSupportClick={() => {
            navigate('/help');
          }}
          onLogout={handleLogout}
          mobileOpen={mobileDrawerOpen}
          onMobileToggle={setMobileDrawerOpen}
          isCollapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      }
      header={
        <Box component="header" sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3.75, pb: 2.5, borderBottom: '2px solid #e1e5e9', gap: 2.5 }}>
          {isMobileScreen && (
            <IconButton
              onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
              size="small"
              sx={{ mr: 1 }}
              aria-label={mobileDrawerOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {mobileDrawerOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'heading.main' }}>{isAccountManagement ? 'Account Management' : isReports ? 'Reports' : isImportData ? 'Import Data' : isExportData ? 'Export Data' : isChannelManagement ? 'Sales Channel Management' : isSalesTransactions ? 'Sales Transactions' : isExpenses ? 'Expenses' : isMileage ? 'Mileage Log' : isHelpAndSupport ? 'Help & Support' : 'Inventory Management'}</Typography>
        </Box>
      }
      isMobileScreen={isMobileScreen}
      sidebarCollapsed={sidebarCollapsed}
      >
      {error && <Alert severity="error" role="alert" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ flex: 1, overflow: 'auto', px: pagePaddingX, py: pagePaddingY }}>
          <AppRoutes
            inventoryProps={{
              user,
              getAuthHeaders,
              pendingCheckout,
              showSuccessMessage,
              setError,
              channels: sharedChannels || [],
              accountId,
              archiveSoldItems: customerDetails?.settings?.find(s => s.key === 'archiveSoldItems')?.value ?? false,
              fetchNextItemNumber,
              isRequestingBarcodes,
              requestBarcodes,
              quailhqStatuses,
            }}
            channelsProps={{
              user,
              channels: sharedChannels || [],
              isLoadingChannels,
              showChannelForm,
              setShowChannelForm,
              channelFormData,
              setChannelFormData,
              editingChannel,
              setEditingChannel,
              fetchChannels: fetchSharedChannels,
              handleChannelInputChange,
              handleChannelSubmit,
              startEditingChannel,
              cancelChannelEdit,
              deleteChannel,
              isSubmitting: isChannelSubmitting,
              needsInitialChannel,
              showSuccessMessage,
              setError,
              integrationStatuses,
              platforms,
              isLoadingStatuses,
              isConnecting,
              isDisconnecting,
              isSyncing,
              isImporting,
              isPulling,
              lastSyncResult,
              lastImportResult,
              lastPullResult,
              conflicts,
              isLoadingConflicts,
              isResolvingConflict,
              fetchPlatforms,
              fetchIntegrationStatuses,
              connectChannel,
              disconnectChannel,
              syncInventory,
              importSales,
              pullInventory,
              fetchConflicts,
              resolveConflict,
              resolveAllConflicts,
              clearResults,
              quotaUsage,
              fetchQuotaUsage,
              quailhqStatuses,
              isLoadingQuailHQStatus,
              fetchQuailHQStatus,
              isRequestingBarcodes,
              requestBarcodes,
            }}
            reportsProps={{
              getAuthHeaders,
              user,
              channels: sharedChannels || [],
            }}
            accountProps={{
              subscriptionStatus,
              customerDetails,
              getAuthHeaders,
              fetchCustomerDetails,
              quotaUsage,
              loadingQuota,
              fetchQuotaUsage,
            }}
            salesTransactionsProps={{
              transactions,
              isLoading: isLoadingTransactions,
              error: transactionsError,
              channels: sharedChannels || [],
              onUpdateDate: updateTransactionDate,
              selectedMonth: transactionsMonth,
              setSelectedMonth: setTransactionsMonth,
              monthOptions: transactionsMonthOptions,
              selectedChannel: transactionsChannel,
              setSelectedChannel: setTransactionsChannel,
            }}
            expensesProps={{
              user,
              expenses,
              isLoadingExpenses,
              expensesError,
              setExpensesError,
              fetchExpenses,
              createExpense,
              updateExpense,
              deleteExpense: deleteExpenseFn,
              channels: sharedChannels || [],
              selectedMonth: expensesMonth,
              setSelectedMonth: setExpensesMonth,
              monthOptions: expensesMonthOptions,
            }}
            mileageProps={{
              user,
              trips: mileageTrips,
              odometerYearStart,
              odometerYearEnd,
              defaultMileageRate,
              isLoading: isLoadingMileage,
              error: mileageError,
              setError: setMileageError,
              selectedYear: mileageYear,
              setSelectedYear: setMileageYear,
              fetchMileage,
              createTrip,
              updateTrip,
              deleteTrip: deleteTripFn,
              updateOdometer,
              updateMileageRate,
            }}
            importDataProps={{
              user,
              getAuthHeaders,
              channels: sharedChannels || [],
              accountId,
            }}
            exportDataProps={{
              onExport: handleExportData,
            }}
            helpProps={{
              getAuthHeaders,
            }}
            subscriptionProps={{
              isCreatingCheckout,
              onStartTrial: (priceId) => createCheckoutSession(priceId),
              onLogout: handleLogout,
            }}
          />
        </Box>
      <SuccessPopover showSuccess={showSuccess} successMessage={successMessage} />

      </AppLayout>
      <React.Suspense fallback={null}><AIAssistant user={user} getAuthHeaders={getAuthHeaders} agentEnabled={customerDetails?.agentEnabled ?? false} /></React.Suspense>
    </AuthRequired>
  );
};

export default ShopKeepApp;